/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { LuisStepperStep, LuisStepperStepType } from 'src/core/components/luisStepper/LuisStepperStep';
import { getStylist } from 'src/core/themes/luisStylist';

export type LuisStepperProps = {
    /**
     * The list of steps to navigate through.
     */
    steps: LuisStepperStepType[];

    /**
     * Denotes whether the stepper is a parent
     * (depth 0) or an inner stepper (depth > 0)
     */
    depth?: number;

    /**
     * Indicates if the steps aligning is
     * horizontal or vertical.
     */
    horizontal?: boolean;

    /**
     * A callback fires when a step is clicked.
     */
    onStepClick?: (stepKey: string) => void;
};

const { styleDiv } = getStylist('LuisStepper');
const StepContainer = styleDiv<{ width: number }>('StepContainer', {}, ({ customProps }) => ({ width: `${customProps.width}%` }));

/**
 * Represents the stepper component with ability to be horizontal or vertical.
 */
export const LuisStepper = (props: LuisStepperProps) => {
    const { horizontal, steps, depth = 0, onStepClick } = props;

    return (
        <Stack horizontal={horizontal}>
            {steps.map((step, index) => (
                <StepContainer key={step.id} customProps={horizontal && { width: 100 / steps.length }}>
                    <LuisStepperStep
                        {...step}
                        id={step.id}
                        index={index}
                        depth={depth}
                        horizontal={horizontal}
                        stepsCount={steps.length}
                        onStepClick={onStepClick}
                    />
                </StepContainer>
            ))}
        </Stack>
    );
};
